/**
 * How fresh the board's data is, as data.
 *
 * React-free on purpose, and this file owns POLL_MS rather than `useBoard` so that the
 * dependency points this way: the root `vitest` globs the whole repo, and a test that
 * reaches a module importing React fails in CI where `web/` is installed after the test
 * step. `viewPrefs.ts` records that lesson at length; this is it applied.
 */

import { agoLabel } from './relativeTime';

/**
 * Two users and a handful of agents, on a board that changes a few times an hour at most.
 * Fast enough that an agent's write shows up while you are still looking at the board;
 * slow enough that a tab left open all day is a rounding error.
 */
export const POLL_MS = 30_000;

/**
 * Two missed polls, plus the one that would be in flight, before the age badge stops
 * being a freshness note and becomes a warning. Derived rather than written out, because
 * a hardcoded cutoff silently becomes a permanent false alarm the moment POLL_MS moves —
 * on the one control whose entire job is to be trusted.
 */
export const STALE_AFTER_MS = POLL_MS * 3;

export interface Freshness {
  label: string;
  stale: boolean;
}

export function describeFreshness(at: number, now: number): Freshness {
  // A clock that has jumped backwards makes this negative, which reads as fresh — the
  // right answer for a board that was polled moments ago. `agoLabel` clamps the wording.
  const elapsed = now - at;
  return { label: agoLabel(elapsed), stale: elapsed > STALE_AFTER_MS };
}

const GREEN_HUE = 145;

/**
 * Hue for the freshness dot: green while polls are landing, warming through amber to red
 * as the data ages toward the stale cutoff.
 *
 * The fade runs over STALE_AFTER_MS rather than over one poll period, which is the
 * tempting reading. Over a single period the dot would reach red just before every
 * SUCCESSFUL poll and snap back — an alarm firing on the healthy path, several times a
 * minute, which is the fastest way to teach someone to ignore it. Across the cutoff it
 * sits green in normal use and only warms when polls are genuinely being missed.
 */
export function freshnessHue(at: number, now: number): number {
  const elapsed = Math.max(0, now - at);
  const fraction = Math.min(1, elapsed / STALE_AFTER_MS);
  return Math.round(GREEN_HUE * (1 - fraction));
}

/**
 * Mid lightness on purpose: the bar renders on the page background, which follows the
 * viewer's light/dark theme, and a colour tuned for one washes out on the other.
 */
export const freshnessColor = (at: number, now: number) =>
  `hsl(${freshnessHue(at, now)} 65% 45%)`;

/**
 * Whether an in-flight refresh should hold the dot green.
 *
 * Bounded to one poll period, and that bound is the whole point. `fetch` has no timeout
 * and the in-flight guard blocks later polls, so a request that hangs — a laptop woken
 * with a dead socket is the ordinary way — would otherwise suppress the alarm for as long
 * as it hung, showing a healthy green over a maximally stale board. A refresh that has
 * outlived a poll period is a symptom, not a reassurance.
 */
export function isSettling(refreshingSince: number | null, now: number): boolean {
  return refreshingSince !== null && now - refreshingSince < POLL_MS;
}
